"use client";

import { Nav } from "@/components/ui/nav";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { LangToggle } from "@/components/ui/lang-toggle";
import Image from "next/image";
import Link from "next/link";
import { useLanguage } from "@/lib/i18n/context";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import { useResolvedTheme } from "@/lib/hooks/use-resolved-theme";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function Header() {
    const { t } = useLanguage();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const pathname = usePathname();
    const { mounted, isLight } = useResolvedTheme();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
    }, [pathname]);


    const shouldShowDarkText = isScrolled || (mounted && isLight);

    return (
        <>
            <header className={`fixed top-0 left-0 right-0 z-50 flex justify-center transition-all duration-500 ${isScrolled ? "pt-4" : "pt-0"}`}>
                <div
                    className={`transition-all duration-500 ease-in-out flex items-center justify-center
                        ${isScrolled
                            ? "max-w-5xl w-[95%] sm:w-[90%] h-16 rounded-full border border-zinc-300/60 bg-stone-100/80 backdrop-blur-xl shadow-2xl shadow-zinc-950/5 ring-1 ring-zinc-900/5 dark:border-white/12 dark:bg-zinc-950/80 dark:ring-white/10 dark:shadow-black/40"
                            : "w-full h-20 border-b border-transparent bg-transparent"
                        }`}
                >
                    <div className={`flex items-center justify-between w-full max-w-5xl h-full transition-all duration-500
                        ${isScrolled ? "pl-5 pr-3" : "px-6"}
                    `}>
                <Link href="/" className="flex items-center transition-all hover:opacity-80 group">
                            <div className="relative h-10 w-44 transition-all duration-500 sm:h-12 sm:w-52">
                                <Image
                                    src="/media/logos/horizontal-logo.svg"
                                    alt="Automatyka CNC"
                                    fill
                                    className="object-contain transition-all duration-500"
                                    priority
                                />
                            </div>
                        </Link>

                        <div className="hidden lg:flex flex-1 items-center justify-center">
                            <Nav instanceId="desktop" />
                        </div>

                        <div className="flex items-center gap-2">
                            <div className={`flex items-center p-1 rounded-full transition-all duration-500
                                ${isScrolled
                                    ? "bg-zinc-200/50 dark:bg-zinc-800/50 backdrop-blur-md"
                                    : "bg-transparent"
                                }`}
                            >
                                <div className="hidden sm:flex items-center">
                                    <LangToggle />
                                    <div className="w-px h-5 bg-zinc-300 dark:bg-zinc-700 mx-1" />
                                </div>
                                <ThemeToggle />
                            </div>

                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`lg:hidden p-2 rounded-full transition-all duration-300
                                    ${isScrolled 
                                        ? "bg-orange-500 text-white" 
                                        : (shouldShowDarkText ? "text-zinc-900" : "text-white")
                                    }`}
                                aria-label="Toggle Menu"
                            >
                                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-0 z-40 lg:hidden pt-32 pb-12 px-8 bg-stone-100/95 dark:bg-zinc-950/95 backdrop-blur-3xl flex flex-col items-start overflow-y-auto"
                    >
                        <div className="w-full h-full flex flex-col">
                             <div className="flex w-full justify-between items-center mb-16">
                                <span className="text-xs font-mono opacity-50 uppercase tracking-widest">{t.nav.home} / Menu</span>
                                <LangToggle />
                             </div>
                             
                             <Nav vertical instanceId="mobile" onItemClick={() => setIsMenuOpen(false)} />
                             
                             <div className="mt-auto pt-12 flex flex-col gap-4">
                                 <div className="h-px w-full bg-zinc-200 dark:bg-zinc-800" />
                                 <div className="flex justify-between items-center text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">
                                     <span>© {new Date().getFullYear()}</span>
                                     <span>Automatyka CNC</span>
                                 </div>
                             </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
